import { useState } from 'react';
import { useForm } from '@tanstack/react-form';

import { FormDialog } from '@/components/composed/FormDialog';
import { FormField } from '@/components/composed/FormField';
import { fieldErrorMessages } from '@/lib/forms/field-errors';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Field,
  FieldLabel,
  FieldLegend,
  FieldSet,
} from '@/components/ui/field';
import { Checkbox } from '@/components/ui/checkbox';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { ApiError } from '@/lib/api/errors';
import {
  deactivatedCollisionFromError,
  type DeactivatedCollision,
  type OrgKind,
} from '@/lib/api/domains/users';
import { roleOptionsForOrgKind } from '../role-options';
import {
  addUserFormSchema,
  type AddUserFormValues,
} from '../schemas/add-user-form';
import { useAddUser, useReactivateUser } from '../api/user-mutations';

/*
 * The add-user composer (F02 R16/R18). Builds the task-5.2 behaviour against the
 * task-5.1 tests:
 *
 *   - AC-23bf376f — the role chooser offers ONLY the org kind's roles. The
 *     options come from `roleOptionsForOrgKind(orgKind)` (the courtesy half);
 *     the rule — refusing a role the kind cannot hold on save — is the domain
 *     service's role↔org-kind pin. A build that only hides is worse than one
 *     that only refuses, so the two must agree.
 *   - AC-79b3d5d7 — no default role. The form starts with `role: null`, so
 *     nothing is preselected and the schema refuses the save until a role is
 *     chosen deliberately ("Choose a role.").
 *   - AC-4aac3d04 — the administrator-privilege checkbox starts unticked;
 *     `isAdministrator` is only ever true when the operator ticks it.
 *   - AC-a094f421 (tpa half) — a per-organization email collision is a 409 the
 *     server returns with NOTHING created. The mutation does not swallow it; we
 *     surface the server's reason WHERE THE OPERATOR WORKS (an alert inside the
 *     dialog) and treat the save as a failure, so the dialog never closes as a
 *     success. The 409's generic refusal text is the server's; we render it, we
 *     do not compose our own cause-specific wording.
 *
 * The Identity halves (required fields AC-ce9ab9a7, per-org uniqueness whatever
 * the capitalization AC-f9511bae, cross-org reuse AC-de0279e0, deactivated-row
 * hold AC-8b1e2068, active-collision 409 AC-70a4a983) are Identity's and are
 * CONSUMED through the server (mocked at its boundary), not re-decided here.
 *
 * Task 5.3 adds the DEACTIVATED-collision branch (R40):
 *   - AC-7e12f915 — WHEN the collision is with a DEACTIVATED account, the dialog
 *     says so and NAMES the person the account belongs to. This is the one
 *     refusal that is NOT generic (AC-7e12f915 is explicit it names the person);
 *     every NON-collision failure stays generic.
 *   - AC-989dd3a3 — the dialog OFFERS to make that account active again (a
 *     "Reactivate <name>" button that invokes the `useReactivateUser` seam with
 *     the account's id) instead of only refusing / forcing a second account.
 *
 * The deactivated profile is recovered from the add-user 409 by the transport's
 * `deactivatedCollisionFromError` (the single place that knows the 409's
 * extension shape). An ACTIVE collision returns null there, so it keeps the
 * task-5.2 generic reason and offers NO reactivation (AC-70a4a983).
 *
 * DEPENDS re-check (BUILT ON ASSUMPTIONS):
 *   - CR-E01-13 (challenge): Identity does NOT identify the colliding row on its
 *     409 today. The deactivated branch assumes CR-E01-13 lands and the 409
 *     carries the colliding deactivated profile. If it is declined or ships a
 *     different shape, the re-map is one place — `deactivatedCollisionFromError`
 *     in lib/api/domains/users — and this composer is unchanged.
 *   - CR-E01-10 (confirm): reactivation restores the set. Here the dialog only
 *     OFFERS reactivation and invokes the seam; the full path and set-restore
 *     are task 7.
 *
 * PII: the deactivated person's NAME is shown in the dialog only; the
 * reactivation request carries the account ID, never the name/email.
 */
export function AddUserDialog({
  open,
  onOpenChange,
  entityId,
  orgKind,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The organization the account is added to. */
  entityId: string;
  /** The organization's kind, which decides the roles the chooser offers. */
  orgKind: OrgKind;
}) {
  const roleOptions = roleOptionsForOrgKind(orgKind);
  // The user list is keyed by the organization id (a plain string here — the
  // domain BFF's `/entities/:id/users` routes are not in openapi.json yet, so
  // there is no `EntityId` brand to thread; ADR 0009).
  const addUser = useAddUser(entityId);
  const reactivateUser = useReactivateUser(entityId);

  // The colliding DEACTIVATED account, when the last add collided with one
  // (R40). Held in state (not just a form error string) because the branch
  // renders a named reactivation affordance, not only a message. Cleared when
  // the operator edits the email, so a stale offer never lingers over a changed
  // address.
  const [deactivatedCollision, setDeactivatedCollision] =
    useState<DeactivatedCollision | null>(null);

  /*
   * There is NO permission-set chooser on create (finding 10; F02 R24 / task 8
   * DECIDED): a new account starts with the baseline set alone — none of its
   * own — and the set is assigned afterwards from the editor
   * (`UserEditorDialog`), replace-not-add (AC-ef201ead). The create body
   * therefore carries only email/displayName/role/isAdministrator; it has no
   * `permissionSet` field. A chooser here would be a no-op reporting success
   * for something it did not do, so it is removed.
   */

  const form = useForm({
    defaultValues: {
      displayName: '',
      email: '',
      // No default role: nothing is preselected, and the schema refuses the
      // save until the operator chooses one deliberately (AC-79b3d5d7).
      role: null,
      // Privilege off unless set deliberately (AC-4aac3d04).
      isAdministrator: false,
    } as AddUserFormValues,
    validators: {
      onChange: addUserFormSchema,
      onSubmitAsync: async ({ value }) => {
        try {
          await addUser.mutateAsync({
            email: value.email,
            displayName: value.displayName,
            // The schema has guaranteed a role was chosen by now; narrow off
            // the form's nullable working value.
            role: value.role as 'tpabilling' | 'client',
            isAdministrator: value.isAdministrator,
          });
          return null;
        } catch (error) {
          // A collision with a DEACTIVATED account (R40): hold the colliding
          // profile so the dialog can say so, NAME the person (AC-7e12f915) and
          // OFFER reactivation (AC-989dd3a3). The form-level message is still
          // set as a fallback for any surface that only reads the error string;
          // the named offer renders from the held profile. An ACTIVE collision
          // returns null here, so it falls through to the generic path below
          // and offers no reactivation (AC-70a4a983).
          const deactivated = deactivatedCollisionFromError(error);
          if (deactivated) {
            setDeactivatedCollision(deactivated);
            return {
              form:
                error instanceof ApiError
                  ? error.message
                  : 'That email address belongs to a deactivated account.',
            };
          }
          // A 409 collision (and any other field-shaped refusal) maps back onto
          // the fields so it renders inline; anything else becomes a form-level
          // message surfaced inside the dialog. Either way the save is treated
          // as a failure — onSubmit (success) never runs — so nothing is shown
          // as created and the dialog does not close (AC-a094f421).
          if (error instanceof ApiError && error.fieldErrors?.length) {
            const fields: Record<string, string> = {};
            for (const fe of error.fieldErrors) fields[fe.field] = fe.message;
            return { fields };
          }
          return {
            form:
              error instanceof ApiError
                ? error.message
                : 'That change could not be saved.',
          };
        }
      },
    },
    // Success only: close the dialog. The collision path throws above and never
    // reaches here, so a failed save never closes as a success.
    onSubmit: () => {
      onOpenChange(false);
    },
  });

  return (
    <FormDialog
      open={open}
      onOpenChange={onOpenChange}
      title="Add user"
      onSubmit={() => void form.handleSubmit()}
      submitLabel="Add"
      pending={addUser.isPending}
    >
      {/* The DEACTIVATED-collision branch (R40): say the address belongs to a
          deactivated account, NAME the person (AC-7e12f915), and OFFER to make
          that account active again instead of forcing a second one
          (AC-989dd3a3). This is the one refusal that names the person; it takes
          precedence over the generic form message below. The reactivation
          request carries the account ID only — the name is shown here, never
          sent. */}
      {deactivatedCollision ? (
        <Alert variant="destructive" role="alert">
          <AlertDescription className="flex flex-col gap-2">
            <span>
              That email address belongs to a deactivated account at this
              organization, held by {deactivatedCollision.displayName}. Rather
              than creating a second account, you can bring that account back.
            </span>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="self-start"
              disabled={reactivateUser.isPending}
              onClick={() => {
                // Invoke the reactivation seam with the account ID (ids in
                // requests; the name is UI-only). On success close the dialog;
                // the full reactivation path/editor is task 7.
                reactivateUser.mutate(deactivatedCollision.userId, {
                  onSuccess: () => {
                    setDeactivatedCollision(null);
                    onOpenChange(false);
                  },
                });
              }}
            >
              {reactivateUser.isPending
                ? 'Reactivating…'
                : `Reactivate ${deactivatedCollision.displayName}`}
            </Button>
            {/* A refused reactivation is surfaced here and the offer stays so
                it can be retried — "nothing fails silently" (finding 11). One
                generic text; the 401/403 interceptor is the backstop. */}
            {reactivateUser.isError ? (
              <span className="text-sm">
                That account could not be reactivated. Please try again.
              </span>
            ) : null}
          </AlertDescription>
        </Alert>
      ) : null}

      {/* The collision reason, surfaced where the operator works. The server's
          refusal text is rendered verbatim; we never compose a cause-specific
          message of our own (one generic refusal regardless of cause). The
          deactivated-collision branch above already shows its own named
          message, so the generic form alert is suppressed while that offer is
          open. */}
      <form.Subscribe selector={(state) => state.errorMap.onSubmit}>
        {(formError) =>
          formError && !deactivatedCollision ? (
            <Alert variant="destructive" role="alert">
              <AlertDescription>
                {typeof formError === 'string'
                  ? formError
                  : String(
                      (formError as { form?: unknown })?.form ?? formError,
                    )}
              </AlertDescription>
            </Alert>
          ) : null
        }
      </form.Subscribe>

      <form.Field name="displayName">
        {(field) => (
          <FormField
            label="Name"
            errors={fieldErrorMessages(field.state.meta.errors)}
          >
            {({ id }) => (
              <Input
                id={id}
                value={field.state.value}
                onChange={(e) => field.handleChange(e.target.value)}
                onBlur={field.handleBlur}
              />
            )}
          </FormField>
        )}
      </form.Field>

      <form.Field name="email">
        {(field) => (
          <FormField
            label="Email"
            errors={fieldErrorMessages(field.state.meta.errors)}
          >
            {({ id }) => (
              <Input
                id={id}
                type="email"
                value={field.state.value}
                onChange={(e) => {
                  // A changed address invalidates any standing deactivated
                  // offer, so it never lingers over a different email.
                  if (deactivatedCollision) setDeactivatedCollision(null);
                  field.handleChange(e.target.value);
                }}
                onBlur={field.handleBlur}
              />
            )}
          </FormField>
        )}
      </form.Field>

      <form.Field name="role">
        {(field) => {
          const errors = fieldErrorMessages(field.state.meta.errors);
          return (
            <FieldSet>
              <FieldLegend variant="label">Role</FieldLegend>
              {/* No default: value is null until a role is chosen, so nothing
                  is preselected (AC-79b3d5d7). The chooser offers only the org
                  kind's roles (AC-23bf376f). */}
              <RadioGroup
                value={field.state.value ?? null}
                onValueChange={(next) =>
                  field.handleChange(next as AddUserFormValues['role'])
                }
                aria-label="Role"
              >
                {roleOptions.map((option) => (
                  <FieldLabel key={option.value}>
                    <Field orientation="horizontal">
                      <RadioGroupItem value={option.value} />
                      <span>{option.label}</span>
                    </Field>
                  </FieldLabel>
                ))}
              </RadioGroup>
              {errors.length > 0 ? (
                <p role="alert" className="text-sm text-destructive">
                  {errors[0]}
                </p>
              ) : null}
            </FieldSet>
          );
        }}
      </form.Field>

      {/* No permission-set chooser on create (finding 10): a new account starts
          with the baseline alone and the set is assigned from the editor
          afterwards (AC-ef201ead). */}

      <form.Field name="isAdministrator">
        {(field) => (
          <Field orientation="horizontal">
            {/* Privilege unticked by default (AC-4aac3d04): only ever true when
                the operator ticks it. */}
            <Checkbox
              checked={field.state.value}
              onCheckedChange={(checked) =>
                field.handleChange(checked === true)
              }
              id="add-user-administrator"
            />
            <FieldLabel htmlFor="add-user-administrator">
              Administrator privilege
            </FieldLabel>
          </Field>
        )}
      </form.Field>
    </FormDialog>
  );
}
